import { useCallback, useEffect, useState } from "react";
import {
  Clock,
  MessageSquare,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  RefreshCw,
  Undo2,
} from "lucide-react";
import { useCallsApi } from "../api/callsApi";
import { useWhatsAppCall } from "../context/WhatsAppCallContext";

const PERIODS = [
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
];

const OUTCOME_LABELS = {
  missed: "Perdida",
  busy: "Perdida (ocupado)",
  rejected_by_agent: "Recusada no CRM",
  declined_on_phone: "Recusada no celular",
};

function formatDuration(totalSeconds) {
  const seconds = Math.max(Math.round(totalSeconds || 0), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours) return `${hours}h${String(minutes).padStart(2, "0")}`;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function formatMinutes(minutes) {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `${hours}h${String(minutes % 60).padStart(2, "0")}` : `${Math.round(hours / 24)} dias`;
}

function formatPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  const match = digits.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : digits;
}

const percent = (part, total) => (total ? `${Math.round((part / total) * 100)}%` : "—");

const Kpi = ({ icon: Icon, tone, value, label, hint, alert }) => (
  <div
    className={`rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900 ${
      alert ? "border-red-300 dark:border-red-800" : "border-gray-200 dark:border-gray-700"
    }`}
  >
    <div className="mb-1 flex items-center justify-between">
      <Icon className={`h-5 w-5 ${tone}`} />
      <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
    </div>
    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
    {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </div>
);

const Card = ({ title, subtitle, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
    <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const DailyChart = ({ days }) => {
  const max = Math.max(1, ...days.map((day) => day.incoming + day.outgoing));
  if (!days.length) {
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Nenhuma ligação no período.</p>;
  }
  return (
    <div className="p-4">
      <div className="flex h-40 items-end gap-1">
        {days.map((day) => {
          const answeredIncoming = day.incoming - day.unattended;
          const scale = (value) => `${(value / max) * 100}%`;
          return (
            <div
              key={day.date}
              className="flex h-full min-w-0 flex-1 flex-col justify-end"
              title={`${day.date.split("-").reverse().join("/")}: ${day.incoming} recebidas (${day.unattended} não atendidas), ${day.outgoing} realizadas`}
            >
              <div className="bg-blue-400 dark:bg-blue-500" style={{ height: scale(day.outgoing) }} />
              <div className="bg-red-400 dark:bg-red-500" style={{ height: scale(day.unattended) }} />
              <div className="rounded-t-sm bg-green-500" style={{ height: scale(answeredIncoming) }} />
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-green-500" /> Recebidas atendidas
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-red-400" /> Recebidas não atendidas
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-400" /> Realizadas
        </span>
      </div>
    </div>
  );
};

const CallsReport = ({ onOpenConversation }) => {
  const api = useCallsApi();
  const { available, canMakeCalls, busy, startCall } = useWhatsAppCall();
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await api.getReport(days));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, days]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !report) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-gray-600 dark:text-gray-400">Carregando ligações...</p>
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-red-500">Erro ao carregar o relatório: {error}</p>
      </div>
    );
  }

  if (!report) return null;
  const { totals, agents, unreturned } = report;
  const unattended = totals.missed + totals.rejected;
  const answeredIncoming = totals.answeredInCrm + totals.answeredOnPhone;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Ligações</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {totals.total} ligações de WhatsApp nos últimos {report.period.days} dias
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => setDays(period.value)}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                    days === period.value
                      ? "bg-green-500 text-white"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm text-white transition-colors hover:bg-green-600 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Kpi
            icon={PhoneIncoming}
            tone="text-green-500"
            value={totals.incoming}
            label="Recebidas"
            hint={`${percent(answeredIncoming, totals.incoming)} atendidas · ${totals.answeredOnPhone} no celular`}
          />
          <Kpi
            icon={PhoneMissed}
            tone="text-red-500"
            value={unattended}
            label="Não atendidas"
            hint={`${totals.missed} perdidas · ${totals.rejected} recusadas`}
            alert={unattended > 0}
          />
          <Kpi
            icon={Undo2}
            tone="text-amber-500"
            value={`${totals.returned}/${unattended}`}
            label="Retornadas"
            hint={`Tempo mediano: ${formatMinutes(totals.medianReturnMinutes)}`}
          />
          <Kpi
            icon={PhoneOutgoing}
            tone="text-blue-500"
            value={totals.outgoing}
            label="Realizadas"
            hint={`${percent(totals.outgoingAnswered, totals.outgoing)} atendidas pelo cliente`}
          />
          <Kpi
            icon={Clock}
            tone="text-purple-500"
            value={formatDuration(totals.talkSeconds)}
            label="Tempo em ligação"
            hint={`Média ${formatDuration(totals.avgDurationSeconds)} por ligação`}
          />
          <Kpi
            icon={PhoneMissed}
            tone="text-red-600"
            value={totals.unreturned}
            label="Sem retorno"
            hint="Não atendidas que ninguém retornou"
            alert={totals.unreturned > 0}
          />
        </div>

        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          <Card title="Por dia">
            <DailyChart days={report.days} />
          </Card>

          <Card
            title="Por atendente"
            subtitle="Tempo e média contam só ligações atendidas pelo CRM. Ligações atendidas no celular não têm atendente."
          >
            {agents.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2">Atendente</th>
                      <th className="px-4 py-2 text-right">Realizadas</th>
                      <th className="px-4 py-2 text-right">Atendeu</th>
                      <th className="px-4 py-2 text-right">Retornos</th>
                      <th className="px-4 py-2 text-right">Tempo</th>
                      <th className="px-4 py-2 text-right">Média</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {agents.map((agent) => (
                      <tr key={agent.userId} className="text-gray-900 dark:text-gray-100">
                        <td className="px-4 py-2 font-medium">{agent.name}</td>
                        <td className="px-4 py-2 text-right" title={`${agent.outgoingAnswered} atendidas pelo cliente`}>
                          {agent.outgoing}
                        </td>
                        <td className="px-4 py-2 text-right">{agent.incomingAnswered}</td>
                        <td className="px-4 py-2 text-right">{agent.returns}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatDuration(agent.talkSeconds)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatDuration(agent.avgDurationSeconds)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                Nenhuma ligação feita ou atendida pelo CRM no período.
              </p>
            )}
          </Card>
        </div>

        <Card
          title="Não atendidas sem retorno"
          subtitle="Conta como retorno a mensagem ou ligação de um atendente pelo CRM, ou uma nova ligação do cliente atendida. Mensagem automática não conta."
        >
          {unreturned.length ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {unreturned.map((call) => (
                <li key={call.callId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                      {call.contactName || formatPhone(call.phone) || "Número não identificado"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(call.startedAt).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      · {OUTCOME_LABELS[call.outcome] || call.outcome}
                      {call.video && " · vídeo"}
                      {call.contactName && call.phone && ` · ${formatPhone(call.phone)}`}
                    </p>
                  </div>
                  {call.conversationId && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => onOpenConversation?.(call.conversationId)}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Abrir conversa
                      </button>
                      {available && canMakeCalls && (
                        <button
                          onClick={() => startCall(call.conversationId)}
                          disabled={busy}
                          className="flex items-center gap-1 rounded-lg bg-green-500 px-3 py-1.5 text-xs text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <PhoneCall className="h-3.5 w-3.5" />
                          Retornar
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
              Nenhuma ligação esperando retorno. 👏
            </p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CallsReport;
